/**
 * Console Log Buffer
 *
 * Captures recent console logs for bug reports.
 * Stores the last N entries in a circular buffer.
 */

interface LogEntry {
  timestamp: string;
  level: 'warn' | 'error';
  message: string;
  /** Stack trace for errors */
  stack?: string;
}

const MAX_BUFFER_SIZE = 50;
const logBuffer: LogEntry[] = [];
let isInitialized = false;

// Store original console methods (only warn and error)
const originalConsole = {
  warn: console.warn,
  error: console.error
};

/**
 * Safely stringify any value for logging
 */
function safeStringify(value: unknown): string {
  if (value === null) {
return 'null';
}
  if (value === undefined) {
return 'undefined';
}
  if (typeof value === 'string') {
return value;
}
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Format arguments into a single message string
 */
function formatArgs(args: unknown[]): string {
  return args.map(safeStringify).join(' ');
}

/**
 * Add an entry to the buffer (circular)
 */
function addToBuffer(entry: LogEntry): void {
  if (logBuffer.length >= MAX_BUFFER_SIZE) {
    logBuffer.shift(); // Remove oldest entry
  }
  logBuffer.push(entry);
}

/**
 * Create an interceptor for a console method
 */
function createInterceptor(
  level: LogEntry['level'],
  original: (...args: unknown[]) => void
): (...args: unknown[]) => void {
  return (...args: unknown[]) => {
    // Skip certain noise
    const message = formatArgs(args);
    const shouldSkip =
      message.includes('[HMR]') ||
      message.includes('[vite]') ||
      message.includes('Download the React DevTools');

    if (!shouldSkip) {
      const entry: LogEntry = {
        timestamp: new Date().toISOString(),
        level,
        message: message.slice(0, 1000) // Truncate long messages
      };

      // Capture stack trace for errors
      if (level === 'error') {
        const errorArg = args.find(a => a instanceof Error);
        if (errorArg instanceof Error && errorArg.stack) {
          entry.stack = errorArg.stack.slice(0, 2000);
        }
      }

      addToBuffer(entry);
    }

    // Call original method
    original.apply(console, args);
  };
}

/**
 * Initialize console log capture
 * Call this once at app startup
 * Only captures warnings and errors (not log/info)
 */
export function initConsoleLogCapture(): void {
  if (isInitialized) {
return;
}
  isInitialized = true;

  // Only intercept warn and error (not log/info to reduce noise)
  console.warn = createInterceptor('warn', originalConsole.warn);
  console.error = createInterceptor('error', originalConsole.error);
}

/**
 * Get the captured console logs
 * Returns a copy of the buffer
 */
export function getCapturedLogs(): LogEntry[] {
  return [...logBuffer];
}

/**
 * Get logs formatted as a string for display/submission
 */
export function getCapturedLogsAsString(): string {
  if (logBuffer.length === 0) {
    return 'No console logs captured';
  }

  return logBuffer
    .map((entry) => {
      const time = new Date(entry.timestamp).toLocaleTimeString();
      const levelIcon = entry.level === 'error' ? 'ERROR' : 'WARN';
      const stackPart = entry.stack ? `\n   ${entry.stack.split('\n').slice(0, 3).join('\n   ')}` : '';
      return `[${time}] [${levelIcon}] ${entry.message}${stackPart}`;
    })
    .join('\n');
}

/**
 * Clear the log buffer
 */
export function clearLogBuffer(): void {
  logBuffer.length = 0;
}

/**
 * Get the number of entries in the buffer
 */
export function getLogBufferSize(): number {
  return logBuffer.length;
}
